/**
 * CSV Import Wizard
 *
 * Feature: F007 - CSV/Excel Import (Sprint 10)
 * Extended: #588 — EDG-834 (multi-year CSV import)
 *
 * Dialog-based wizard for importing financial data from CSV files.
 * Supports two modes:
 * - Single-year: Upload → Preview → Confirm (imports into one FY period)
 * - Multi-year: Upload → Preview with FY column mapping → Confirm (imports into multiple FY periods)
 */

import { useState, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useBulkImport, useBulkImportMultiYear } from '@/hooks/use-financial-data';
import {
  parseCSV,
  toBulkImportPayload,
  detectCSVShape,
  parseMultiYearCSV,
  toMultiYearBulkImportPayload,
} from './csv-parser';
import type { ParsedCSVRow, ParsedCSVResult, ParsedMultiYearCSVResult } from './csv-parser';

/**
 * Returns the CSV template download URL.
 * When an accountId is provided, returns the dynamic account-specific endpoint.
 * Falls back to the static template when no accountId is available.
 */
export function getTemplateDownloadUrl(accountId: string | undefined): string {
  if (accountId) {
    return `/api/financial/accounts/${accountId}/csv-template`;
  }
  return '/templates/edgeview-financial-template.csv';
}

/** A minimal representation of a financial period for the FY selector. */
export interface FinancialPeriodOption {
  id: string;
  label: string;
}

interface CSVImportWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Default period to pre-select. Still required so callers that don't supply
   * financialPeriods continue to work without breaking (backwards compatible).
   */
  periodId: string;
  accountId?: string;
  onImportComplete?: () => void;
  /**
   * Available financial year periods for the FY selector in the preview step
   * (F004 — #586 AC001). When provided the user can choose which FY to import
   * into before confirming the import. Also used for multi-year column mapping.
   */
  financialPeriods?: FinancialPeriodOption[];
}

type WizardStep = 'upload' | 'preview' | 'importing' | 'success' | 'error';

export function CSVImportWizard({
  open,
  onOpenChange,
  periodId,
  accountId,
  onImportComplete,
  financialPeriods,
}: CSVImportWizardProps) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<WizardStep>('upload');
  const [parsedData, setParsedData] = useState<ParsedCSVResult | null>(null);
  const [multiYearData, setMultiYearData] = useState<ParsedMultiYearCSVResult | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [importResult, setImportResult] = useState<{ profitLoss: number; balanceSheet: number } | null>(null);
  const [multiYearResult, setMultiYearResult] = useState<Record<string, { created: { profitLoss: number; balanceSheet: number } }> | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  // FY selector state — defaults to the prop periodId (F004 — #586 AC001)
  const [selectedPeriodId, setSelectedPeriodId] = useState<string>(periodId);
  // Multi-year FY column → period ID mapping (#588)
  const [fyColumnMapping, setFyColumnMapping] = useState<Record<string, string>>({});

  const bulkImport = useBulkImport();
  const bulkImportMultiYear = useBulkImportMultiYear();

  const isMultiYear = multiYearData !== null;

  const resetState = useCallback(() => {
    setStep('upload');
    setParsedData(null);
    setMultiYearData(null);
    setFileName('');
    setImportResult(null);
    setMultiYearResult(null);
    setErrorMessage('');
    setSelectedPeriodId(periodId);
    setFyColumnMapping({});
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [periodId]);

  const handleClose = useCallback(() => {
    resetState();
    onOpenChange(false);
  }, [resetState, onOpenChange]);

  const handleFileSelect = useCallback(async (file: File) => {
    if (!file.name.endsWith('.csv')) {
      toast({
        title: 'Invalid file type',
        description: 'Please select a CSV file (.csv)',
        variant: 'destructive',
      });
      return;
    }

    try {
      const text = await file.text();

      // Strip BOM for shape detection
      let headerText = text;
      if (headerText.charCodeAt(0) === 0xFEFF) {
        headerText = headerText.slice(1);
      }
      // Find first non-comment line for shape detection
      const headerLines = headerText.replace(/\r\n/g, '\n').split('\n');
      let headerLine = '';
      for (const line of headerLines) {
        if (!line.trim().startsWith('#') && line.trim()) {
          headerLine = line;
          break;
        }
      }

      const shape = detectCSVShape(headerLine);

      if (shape.type === 'multi-year') {
        const result = parseMultiYearCSV(text);
        if (result.rows.length === 0) {
          toast({
            title: 'No data found',
            description: 'The CSV file contains no valid financial data rows.',
            variant: 'destructive',
          });
          return;
        }
        setMultiYearData(result);
        setParsedData(null);

        // Auto-map FY columns to periods by matching labels
        if (financialPeriods) {
          const mapping: Record<string, string> = {};
          for (const fy of result.fyColumns) {
            const match = financialPeriods.find(
              (p) => p.label.toUpperCase().includes(fy.toUpperCase())
            );
            if (match) {
              mapping[fy] = match.id;
            }
          }
          setFyColumnMapping(mapping);
        }
      } else {
        const result = parseCSV(text);
        if (result.rows.length === 0) {
          toast({
            title: 'No data found',
            description: 'The CSV file contains no valid financial data rows.',
            variant: 'destructive',
          });
          return;
        }
        setParsedData(result);
        setMultiYearData(null);
      }

      setFileName(file.name);
      setStep('preview');
    } catch {
      toast({
        title: 'Parse error',
        description: 'Failed to parse the CSV file. Please check the format.',
        variant: 'destructive',
      });
    }
  }, [toast, financialPeriods]);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      const file = e.dataTransfer.files[0];
      if (file) handleFileSelect(file);
    },
    [handleFileSelect]
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFileSelect(file);
    },
    [handleFileSelect]
  );

  // Single-year import handler
  const handleImport = useCallback(async () => {
    if (!parsedData || !selectedPeriodId) return;

    setStep('importing');
    const payload = toBulkImportPayload(parsedData.rows);

    try {
      const body = await bulkImport.mutateAsync({ periodId: selectedPeriodId, payload });

      setImportResult({
        profitLoss: body.created.profitLoss,
        balanceSheet: body.created.balanceSheet,
      });
      setStep('success');

      toast({
        title: 'Import successful',
        description: `Imported ${body.created.profitLoss} P&L items and ${body.created.balanceSheet} BS items`,
      });

      onImportComplete?.();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Import failed';
      setErrorMessage(message);
      setStep('error');

      toast({
        title: 'Import failed',
        description: message,
        variant: 'destructive',
      });
    }
  }, [parsedData, selectedPeriodId, bulkImport, toast, onImportComplete]);

  // Multi-year import handler (#588)
  const handleMultiYearImport = useCallback(async () => {
    if (!multiYearData) return;

    // Validate all FY columns are mapped
    const unmappedColumns = multiYearData.fyColumns.filter((fy) => !fyColumnMapping[fy]);
    if (unmappedColumns.length > 0) {
      toast({
        title: 'Unmapped FY columns',
        description: `Please map all FY columns to periods: ${unmappedColumns.join(', ')}`,
        variant: 'destructive',
      });
      return;
    }

    setStep('importing');

    const perFyPayloads = toMultiYearBulkImportPayload(multiYearData.rows, multiYearData.fyColumns);

    try {
      const body = await bulkImportMultiYear.mutateAsync({
        periods: fyColumnMapping,
        items: perFyPayloads,
      });

      setMultiYearResult(body.results);
      setStep('success');

      // Sum totals across all FYs
      let totalPl = 0;
      let totalBs = 0;
      for (const r of Object.values(body.results)) {
        totalPl += r.created.profitLoss;
        totalBs += r.created.balanceSheet;
      }

      toast({
        title: 'Multi-year import successful',
        description: `Imported ${totalPl} P&L and ${totalBs} BS items across ${Object.keys(body.results).length} periods`,
      });

      onImportComplete?.();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Import failed';
      setErrorMessage(message);
      setStep('error');

      toast({
        title: 'Multi-year import failed',
        description: message,
        variant: 'destructive',
      });
    }
  }, [multiYearData, fyColumnMapping, bulkImportMultiYear, toast, onImportComplete]);

  const handleUpdateRowType = useCallback(
    (index: number, newType: string) => {
      if (!parsedData) return;
      const updatedRows = [...parsedData.rows];
      updatedRows[index] = {
        ...updatedRows[index],
        lineItemType: newType,
        matched: true,
        category: newType ? 'profitLoss' : updatedRows[index].category,
      };
      setParsedData({
        ...parsedData,
        rows: updatedRows,
      });
    },
    [parsedData]
  );

  // Count total items for multi-year import
  const multiYearTotalItems = multiYearData
    ? multiYearData.rows.filter((r) => r.matched).length * multiYearData.fyColumns.length
    : 0;

  // Check if all FY columns are mapped
  const allFyColumnsMapped = multiYearData
    ? multiYearData.fyColumns.every((fy) => fyColumnMapping[fy])
    : false;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="csv-import-wizard">
        <DialogHeader>
          <DialogTitle>CSV/Excel Import</DialogTitle>
          <DialogDescription>
            {isMultiYear
              ? 'Import financial data across multiple financial years from a single CSV file'
              : 'Import financial data from a CSV file'}
          </DialogDescription>
        </DialogHeader>

        {step === 'upload' && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <a
                href={getTemplateDownloadUrl(accountId)}
                download="edgeview-financial-template.csv"
                className="text-sm text-blue-600 hover:text-blue-800 underline"
                data-testid="download-template-link"
              >
                Download Template
              </a>
            </div>

            <div
              className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-gray-400 transition-colors"
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onClick={() => fileInputRef.current?.click()}
              data-testid="csv-dropzone"
            >
              <p className="text-gray-500">Drop CSV file here or click to browse</p>
              <p className="text-xs text-gray-400 mt-1">Supports single-year and multi-year CSV formats</p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                className="hidden"
                onChange={handleFileInputChange}
                data-testid="csv-file-input"
              />
            </div>
          </div>
        )}

        {/* Single-year preview */}
        {step === 'preview' && parsedData && !isMultiYear && (
          <div className="space-y-4">
            {/* FY Period Selector (F004 — #586 AC001) */}
            {financialPeriods && financialPeriods.length > 0 && (
              <div className="flex items-center gap-2">
                <label htmlFor="fy-period-select" className="text-sm font-medium text-gray-700 whitespace-nowrap">
                  Import into FY:
                </label>
                <select
                  id="fy-period-select"
                  className="text-sm border rounded px-2 py-1 flex-1"
                  value={selectedPeriodId}
                  onChange={(e) => setSelectedPeriodId(e.target.value)}
                  data-testid="fy-period-select"
                >
                  {financialPeriods.map((period) => (
                    <option key={period.id} value={period.id}>
                      {period.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="text-sm text-gray-600">
              File: <span className="font-medium">{fileName}</span> |{' '}
              {parsedData.summary.profitLossCount} P&L items |{' '}
              {parsedData.summary.balanceSheetCount} BS items
              {parsedData.summary.unmatchedCount > 0 && (
                <span className="text-amber-600">
                  {' '}| {parsedData.summary.unmatchedCount} unmatched
                </span>
              )}
            </div>

            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm" data-testid="csv-preview-table">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="px-3 py-2 text-left font-medium">Line Item</th>
                    <th className="px-3 py-2 text-left font-medium">Amount</th>
                    <th className="px-3 py-2 text-left font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {parsedData.rows.map((row: ParsedCSVRow, index: number) => (
                    <tr
                      key={index}
                      className={`border-b ${!row.matched ? 'bg-amber-50' : ''}`}
                      data-testid={`csv-preview-row-${index}`}
                    >
                      <td className="px-3 py-2">
                        {row.matched ? (
                          <span>{row.label || row.lineItemType}</span>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className="text-amber-600">{row.label || row.lineItemType}</span>
                            <select
                              className="text-xs border rounded px-1 py-0.5"
                              value={row.lineItemType}
                              onChange={(e) => handleUpdateRowType(index, e.target.value)}
                              data-testid={`csv-remap-select-${index}`}
                            >
                              <option value={row.lineItemType}>Manual assign...</option>
                            </select>
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono">
                        {new Intl.NumberFormat('en-AU', {
                          style: 'currency',
                          currency: 'AUD',
                          minimumFractionDigits: 0,
                        }).format(row.amount)}
                      </td>
                      <td className="px-3 py-2">
                        {row.matched ? (
                          <span className="text-green-600" data-testid={`csv-status-matched-${index}`}>Matched</span>
                        ) : (
                          <span className="text-amber-600" data-testid={`csv-status-unmatched-${index}`}>Unmatched</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={resetState} data-testid="csv-back-button">
                Back
              </Button>
              <Button onClick={handleImport} data-testid="csv-import-button">
                Import {parsedData.summary.totalRows} items
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Multi-year preview (#588) */}
        {step === 'preview' && multiYearData && isMultiYear && (
          <div className="space-y-4" data-testid="csv-multi-year-preview">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800 font-medium">
                Multi-year import detected: {multiYearData.fyColumns.length} financial years
              </p>
              <p className="text-xs text-blue-600 mt-1">
                {multiYearData.fyColumns.join(' | ')}
              </p>
            </div>

            {/* FY Column → Period Mapping */}
            {financialPeriods && financialPeriods.length > 0 && (
              <div className="space-y-2" data-testid="csv-fy-column-mapping">
                <p className="text-sm font-medium text-gray-700">Map each column to a financial period:</p>
                {multiYearData.fyColumns.map((fy) => (
                  <div key={fy} className="flex items-center gap-2">
                    <span className="text-sm font-mono w-20">{fy}</span>
                    <span className="text-gray-400 text-sm">&rarr;</span>
                    <select
                      className="text-sm border rounded px-2 py-1 flex-1"
                      value={fyColumnMapping[fy] || ''}
                      onChange={(e) => setFyColumnMapping((prev) => ({ ...prev, [fy]: e.target.value }))}
                      data-testid={`csv-fy-map-${fy}`}
                    >
                      <option value="">Select period...</option>
                      {financialPeriods.map((period) => (
                        <option key={period.id} value={period.id}>
                          {period.label}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            )}

            <div className="text-sm text-gray-600">
              File: <span className="font-medium">{fileName}</span> |{' '}
              {multiYearData.summary.profitLossCount} P&L items |{' '}
              {multiYearData.summary.balanceSheetCount} BS items
              {multiYearData.summary.unmatchedCount > 0 && (
                <span className="text-amber-600">
                  {' '}| {multiYearData.summary.unmatchedCount} unmatched
                </span>
              )}
            </div>

            <div className="border rounded-lg overflow-hidden overflow-x-auto">
              <table className="w-full text-sm" data-testid="csv-multi-year-preview-table">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="px-3 py-2 text-left font-medium">Line Item</th>
                    {multiYearData.fyColumns.map((fy) => (
                      <th key={fy} className="px-3 py-2 text-right font-medium">{fy}</th>
                    ))}
                    <th className="px-3 py-2 text-left font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {multiYearData.rows.map((row, index) => (
                    <tr
                      key={index}
                      className={`border-b ${!row.matched ? 'bg-amber-50' : ''}`}
                      data-testid={`csv-multi-year-row-${index}`}
                    >
                      <td className="px-3 py-2">
                        <span className={!row.matched ? 'text-amber-600' : ''}>
                          {row.label || row.lineItemType}
                        </span>
                      </td>
                      {multiYearData.fyColumns.map((fy) => (
                        <td key={fy} className="px-3 py-2 font-mono text-right">
                          {new Intl.NumberFormat('en-AU', {
                            style: 'currency',
                            currency: 'AUD',
                            minimumFractionDigits: 0,
                          }).format(row.amounts[fy] || 0)}
                        </td>
                      ))}
                      <td className="px-3 py-2">
                        {row.matched ? (
                          <span className="text-green-600">Matched</span>
                        ) : (
                          <span className="text-amber-600">Unmatched</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={resetState} data-testid="csv-back-button">
                Back
              </Button>
              <Button
                onClick={handleMultiYearImport}
                disabled={!allFyColumnsMapped}
                data-testid="csv-multi-year-import-button"
              >
                Import {multiYearTotalItems} items across {multiYearData.fyColumns.length} FYs
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 'importing' && (
          <div className="py-8 text-center" data-testid="csv-importing-state">
            <p className="text-gray-500">
              {isMultiYear ? 'Importing financial data across multiple periods...' : 'Importing financial data...'}
            </p>
          </div>
        )}

        {step === 'success' && !isMultiYear && importResult && (
          <div className="py-8 text-center space-y-4" data-testid="csv-success-state">
            <p className="text-green-600 font-medium">Import complete</p>
            <p className="text-sm text-gray-600">
              Imported {importResult.profitLoss} P&L items and{' '}
              {importResult.balanceSheet} BS items
            </p>
            <DialogFooter>
              <Button onClick={handleClose} data-testid="csv-done-button">
                Done
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 'success' && isMultiYear && multiYearResult && (
          <div className="py-8 text-center space-y-4" data-testid="csv-multi-year-success-state">
            <p className="text-green-600 font-medium">Multi-year import complete</p>
            <div className="text-sm text-gray-600 space-y-1">
              {Object.entries(multiYearResult).map(([fy, result]) => (
                <p key={fy}>
                  {fy}: {result.created.profitLoss} P&L + {result.created.balanceSheet} BS items
                </p>
              ))}
            </div>
            <DialogFooter>
              <Button onClick={handleClose} data-testid="csv-done-button">
                Done
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 'error' && (
          <div className="py-8 text-center space-y-4" data-testid="csv-error-state">
            <p className="text-red-600 font-medium">Import failed</p>
            <p className="text-sm text-gray-600">{errorMessage}</p>
            <DialogFooter>
              <Button variant="outline" onClick={resetState} data-testid="csv-retry-button">
                Try Again
              </Button>
              <Button onClick={handleClose}>Close</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
